import React from 'react';
import { Badge, Spinner } from 'react-bootstrap';

import Accordion from 'components/Accordion';
import Button from 'components/DesignSystem/Button';
import TitleSemiBold from 'components/Typography/TitleSemiBold';
import { PainelOptions } from 'helper/windx';
import { LIMITE_SIMULACOES_ANTIGAS_ATIVAS } from 'redux/reducers/windxControlPanelReducer';

import ModalCadastrar from './ModalCadastrar';
import SimulacaoItem from './SimulacaoItem';
import useSimulacoesAntigasSalvas, { LIMITE_POR_USUARIO } from './useSimulacoesAntigasSalvas';

interface SimulacoesAntigasSalvasProps {
  opts: PainelOptions;
  dataMinima: string; // ISO YYYY-MM-DD (D-14)
  dataMaxima: string; // ISO YYYY-MM-DD (D-1)
}

/**
 * Accordion "Incluir Simulações Antigas" (FE-WINDX-043 v7). **Apenas WEOL-SH.**
 *
 * - Lista de simulacoes salvas com **checkbox multi** (era radio em v6).
 *   Usuario pode marcar 1..3 sims ao mesmo tempo — cada uma adiciona suas
 *   curvas ao grafico, em tracejado, com sufixo `[nome-da-sim]` na legenda.
 * - Limite 3 enforced no reducer (toggleSimulacaoAntigaAtiva) E na UI
 *   (prop `bloqueado` desabilita inativas quando ja ha 3 marcadas).
 * - Removido o radio "Apenas Estudo Automatico" (v6) — sem ele o estado
 *   default ja eh array vazio.
 * - Badge no accordion header mostra qty ativas (`N ativa(s)`).
 *
 * Persistencia: BE usa `dias_atras` SMALLINT (offset relativo 1..14).
 * Resolucao para data absoluta acontece NO HOOK useChartDataMergedSH
 * (1 chamada por sim).
 */
export default function SimulacoesAntigasSalvas({
  opts,
  dataMinima,
  dataMaxima,
}: SimulacoesAntigasSalvasProps) {
  const {
    items, isLoading, isDeleting, idsAtivos,
    atingiuLimiteAtivas, atingiuLimiteSalvas, modal,
    abrirCadastro, handleToggle, handleEditar, handleDeletar, handleCloseModal,
  } = useSimulacoesAntigasSalvas();

  return (
    <>
      <Accordion.Item eventKey="simulacoes-antigas-salvas">
        <Accordion.Header>
          <TitleSemiBold>Incluir Simulações Antigas</TitleSemiBold>
          {idsAtivos.length > 0 && (
            <Badge
              bg="info"
              pill
              className="ms-2"
              aria-label={
                idsAtivos.length === 1
                  ? '1 simulação antiga ativa'
                  : `${idsAtivos.length} simulações antigas ativas`
              }
            >
              {idsAtivos.length}
            </Badge>
          )}
        </Accordion.Header>
        <Accordion.Body>
          {isLoading && (
            <div className="text-center py-2">
              <Spinner
                animation="border"
                size="sm"
                role="status"
                aria-label="Carregando simulações antigas"
              />
            </div>
          )}

          {!isLoading && items.length === 0 && (
            <p className="text-muted small mb-2">
              Nenhuma simulação antiga salva. Clique em
              {' '}
              <strong>Cadastrar</strong>
              {' '}
              para criar.
            </p>
          )}

          {items.map((sim) => (
            <SimulacaoItem
              key={sim.id}
              sim={sim}
              ativa={idsAtivos.includes(sim.id)}
              bloqueado={atingiuLimiteAtivas}
              isDeleting={isDeleting}
              onToggle={handleToggle}
              onEditar={handleEditar}
              onDeletar={handleDeletar}
            />
          ))}

          {atingiuLimiteAtivas && (
            <small className="text-muted d-block text-center mt-1">
              {`Máximo ${LIMITE_SIMULACOES_ANTIGAS_ATIVAS} simulações ativas simultaneamente.`}
            </small>
          )}

          <div className="d-flex justify-content-center mt-3">
            <Button
              type="primary"
              className="btn-windx-simant-cadastrar text-white px-4"
              disabled={atingiuLimiteSalvas}
              onClick={() => abrirCadastro()}
            >
              + Cadastrar
            </Button>
          </div>
          {atingiuLimiteSalvas && (
            <small className="text-muted d-block text-center mt-1">
              {`Limite de ${LIMITE_POR_USUARIO} simulações salvas atingido. Exclua uma para criar nova.`}
            </small>
          )}
        </Accordion.Body>
      </Accordion.Item>

      {modal.tipo !== 'fechado' && (
        <ModalCadastrar
          open
          initialValue={modal.tipo === 'editar' ? modal.sim : undefined}
          opts={opts}
          dataMinima={dataMinima}
          dataMaxima={dataMaxima}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
}
